import * as React from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { EditInBulkPageDocument, SchemaDataFieldsDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NuvoImporter from 'modules/universal_importer/components/NuvoImporter'
import {
  UnifiedEditionModal,
  type UnifiedEditionModalResult,
  type LegalEntityInfo,
  type EmployeeInfo,
} from 'modules/universal_importer/components/UnifiedEditionModal'
import type { FieldSelectionField } from 'modules/universal_importer/components/FieldSelectionModal'
import { useGetMetadata } from 'modules/universal_importer/components/NuvoImporter/useGetMetadata'
import {
  buildLegalEntityCountryMap,
  buildNuvoMetadata,
  getDistinctLeIds,
} from 'modules/universal_importer/lib/multiLeUtils'

type LocationStateType = {
  employeesIds?: string[]
  employees?: Array<{
    id: string
    firstName: string
    lastName: string
    legalEntityId: string | null
  }>
  selectedLegalEntity?: string
  contractMode?: 'all' | 'current'
  modalResult?: UnifiedEditionModalResult
} | null

const IMPORT_TYPE = 'contract_version_edition' as const

const EditContractsInBulkPage = () => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const location = useLocation()
  const navigate = useNavigate()
  const locationState: LocationStateType = location.state
  const employeeIds = React.useMemo(() => locationState?.employeesIds ?? [], [locationState])
  const selectedLegalEntityId = locationState?.selectedLegalEntity
  const only_reference_contracts = locationState?.contractMode !== 'all'

  const { has_dev_universal_importer_field_selection: ffEnabled, loadingFeatures } = useFeatures([
    'dev_universal_importer_field_selection',
  ])

  const { data, loading, error } = useQuery(EditInBulkPageDocument, {
    variables: {
      companyId: company.id,
    },
    fetchPolicy: 'network-only',
    skip: ffEnabled ? employeeIds.length === 0 : !selectedLegalEntityId,
  })

  // === NEW FLOW (FF enabled) ===

  const legalEntities: LegalEntityInfo[] = React.useMemo(
    () =>
      data?.companies.legalEntitiesConnection.nodes.map((le) => ({
        id: le.id,
        legalName: le.legalName,
        country: le.country,
      })) ?? [],
    [data]
  )

  const employees: EmployeeInfo[] = React.useMemo(
    () =>
      locationState?.employees?.map((emp) => ({
        id: emp.id,
        firstName: emp.firstName,
        lastName: emp.lastName,
        legalEntityId: emp.legalEntityId,
      })) ?? [],
    [locationState]
  )

  const countryMap = React.useMemo(() => buildLegalEntityCountryMap(legalEntities), [legalEntities])

  // Fetch schema fields with all LEs from selected employees
  const allLeIds = React.useMemo(() => getDistinctLeIds(employees), [employees])
  const initialMetadata = React.useMemo(
    () => ({
      legal_entity_ids: allLeIds.map(BigInt),
      legal_entity_countries: allLeIds.map((id) => countryMap[id]).filter(Boolean),
      employee_ids: employeeIds.map(BigInt),
      legal_entity_country_map: [JSON.stringify(countryMap)],
      only_reference_contracts: [only_reference_contracts],
    }),
    [allLeIds, countryMap, employeeIds, only_reference_contracts]
  )
  const { metadataInput: initialMetadataInput } = useGetMetadata(initialMetadata)

  const { data: schemaData, loading: schemaLoading } = useQuery(SchemaDataFieldsDocument, {
    variables: {
      companyId: company.id,
      importType: IMPORT_TYPE,
      metadata: initialMetadataInput,
    },
    fetchPolicy: 'network-only',
    skip: !ffEnabled || employees.length === 0 || !!locationState?.modalResult,
  })

  const fields: FieldSelectionField[] = React.useMemo(
    () =>
      schemaData?.universalImporter.schemaAdapters.map((f) => ({
        key: f.key,
        label: f.label,
        identifier: f.identifier,
        hidden: f.hidden,
        leDependant: f.leDependant,
        category: f.category,
      })) ?? [],
    [schemaData]
  )

  const [modalResult, setModalResult] = React.useState<UnifiedEditionModalResult | null>(
    locationState?.modalResult ?? null
  )
  const [showModal, setShowModal] = React.useState(!locationState?.modalResult)

  // Redirect if employees data is missing (direct navigation without state)
  const shouldRedirect = ffEnabled && employees.length === 0 && !locationState?.modalResult
  React.useEffect(() => {
    if (shouldRedirect) {
      navigate('/employees', { replace: true })
    }
  }, [shouldRedirect, navigate])

  const handleConfirm = React.useCallback((result: UnifiedEditionModalResult) => {
    setModalResult(result)
    setShowModal(false)
  }, [])

  const handleClose = React.useCallback(() => {
    navigate(-1)
  }, [navigate])

  const nuvoMetadata = React.useMemo(() => {
    if (!modalResult) return null
    const base = buildNuvoMetadata(
      modalResult.legalEntityIds,
      countryMap,
      modalResult.selectedEmployeeIds
    )
    return {
      ...base,
      only_reference_contracts: [only_reference_contracts],
    }
  }, [modalResult, countryMap, only_reference_contracts])

  // === LEGACY FLOW (FF disabled) ===

  const legacyLegalEntityCountry = data?.companies.legalEntitiesConnection.nodes.find(
    (le) => le.id === selectedLegalEntityId
  )?.country
  const legacyLegalEntitiesIds = React.useMemo(
    () => (selectedLegalEntityId ? [BigInt(selectedLegalEntityId)] : []),
    [selectedLegalEntityId]
  )
  const legacyMetadata = React.useMemo(
    () => ({
      legal_entity_ids: legacyLegalEntitiesIds,
      employee_ids: employeeIds.map(BigInt),
      legal_entity_countries: legacyLegalEntityCountry ? [legacyLegalEntityCountry] : [],
      only_reference_contracts: [only_reference_contracts],
    }),
    [legacyLegalEntitiesIds, employeeIds, legacyLegalEntityCountry, only_reference_contracts]
  )

  const canImportResources = data?.universalImporter._policies.importResources.can
  const canEditContracts = data?.contracts._policies.contractEditContractVersion.can
  const authorized = canImportResources && canEditContracts

  if (loading || loadingFeatures || (ffEnabled && schemaLoading)) {
    return <Loading />
  }

  // === NEW FLOW RENDERING ===
  if (ffEnabled) {
    if (shouldRedirect) {
      return null
    }

    // Show modal while no result
    if (showModal && fields.length > 0 && employees.length > 0) {
      return (
        <UnifiedEditionModal
          isOpen
          onClose={handleClose}
          onConfirm={handleConfirm}
          legalEntities={legalEntities}
          employees={employees}
          fields={fields}
        />
      )
    }

    // No modal result means user closed without confirming
    if (!modalResult || !nuvoMetadata) {
      return (
        <ResponsivePage.Body
          authorized={authorized}
          loading={false}
          error={!employeeIds.length || error}
          showHeader={false}
        >
          <Outlet />
        </ResponsivePage.Body>
      )
    }

    return (
      <ResponsivePage.Body
        authorized={authorized}
        loading={loading}
        error={error}
        showHeader={false}
      >
        <NuvoImporter
          importType={IMPORT_TYPE}
          companyId={company.id}
          locale={locale}
          metadata={nuvoMetadata}
          isEdition
          selectedFields={modalResult.selectedFields}
        />
        <Outlet />
      </ResponsivePage.Body>
    )
  }

  // === LEGACY FLOW RENDERING (FF disabled) ===
  return (
    <ResponsivePage.Body
      authorized={authorized}
      loading={loading}
      error={!selectedLegalEntityId || error}
      showHeader={false}
    >
      <NuvoImporter
        importType={IMPORT_TYPE}
        companyId={company.id}
        locale={locale}
        metadata={legacyMetadata}
        isEdition
      />
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default EditContractsInBulkPage
